import React, { useState, useEffect } from 'react';
import { FileText, HelpCircle, CheckCircle, Calendar, User, Heart, Home, Users, Briefcase, Target, Shield, DollarSign } from 'lucide-react';
import { getAssessment, saveAssessmentSection, completeAssessment } from '../services/beneficiaryAssessmentService';
import { applyProfileEnrichment } from '../services/profileEnrichmentService';

/**
 * Composant d'auto-évaluation pour les bénéficiaires
 * Interface simplifiée avec aide contextuelle
 */
const ClientSelfAssessment = ({ db, client }) => {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('prealables');
  const [completionPercentage, setCompletionPercentage] = useState(0);

  // Sections du questionnaire
  const sections = [
    { id: 'prealables', label: 'Informations générales', icon: FileText },
    { id: 'familialStatus', label: 'Situation familiale', icon: User },
    { id: 'legalProtection', label: 'Protection juridique', icon: Shield },
    { id: 'contacts', label: 'Contacts et soutien', icon: Users },
    { id: 'housing', label: 'Logement', icon: Home },
    { id: 'health', label: 'Santé', icon: Heart },
    { id: 'autonomy', label: 'Autonomie', icon: Target },
    { id: 'psychological', label: 'Bien-être', icon: Heart },
    { id: 'currentAids', label: 'Aides actuelles', icon: DollarSign },
    { id: 'needs', label: 'Besoins', icon: Briefcase },
    { id: 'project', label: 'Votre projet', icon: Target }
  ];

  useEffect(() => {
    loadAssessment();
  }, [client?.id]);

  const loadAssessment = async () => {
    if (!client?.id) return;
    
    try {
      const assessmentData = await getAssessment(db, client.id);
      if (assessmentData) {
        setAssessment(assessmentData);
        calculateCompletion(assessmentData);
      } else {
        // Initialiser un questionnaire vide
        const emptyAssessment = {
          status: 'non_commencé',
          startedAt: new Date(),
          lastUpdatedAt: new Date(),
          prealables: { evaluationDate: null, evaluationDuration: '' },
          health: { generalHealth: '', mainPathologies: '' },
          contacts: { emergencyContact: { name: '', relationship: '', phone1: '' } }
        };
        setAssessment(emptyAssessment);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du questionnaire:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateCompletion = (assessmentData) => {
    if (!assessmentData) return 0;
    const filledSections = sections.filter(section => {
      const sectionData = assessmentData[section.id];
      return sectionData && Object.values(sectionData).some(value => 
        value !== '' && value !== null && value !== false && (Array.isArray(value) ? value.length > 0 : true)
      );
    }).length;
    const percentage = Math.round((filledSections / sections.length) * 100);
    setCompletionPercentage(percentage);
    return percentage;
  };

  const handleFieldChange = (section, field, value) => {
    setAssessment(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      },
      status: 'en_cours',
      lastUpdatedAt: new Date()
    }));
  };

  const saveSection = async (sectionId) => {
    if (!assessment || !client?.id) return;
    
    setSaving(true);
    try {
      await saveAssessmentSection(db, client.id, sectionId, assessment[sectionId]);
      calculateCompletion(assessment);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setSaving(false);
    }
  };

  const completeSelfAssessment = async () => {
    if (!assessment || !client?.id) return;
    
    setSaving(true);
    try {
      await completeAssessment(db, client.id, {
        uid: client.id,
        displayName: `${client.firstName} ${client.lastName}`,
        role: 'beneficiary'
      });

      // Synchroniser automatiquement avec le profil - données médicales complètes
      const enrichmentData = {
        emergencyContacts: assessment.contacts?.emergencyContact?.name ? [{
          name: assessment.contacts.emergencyContact.name,
          relationship: assessment.contacts.emergencyContact.relationship || '',
          phone1: assessment.contacts.emergencyContact.phone1 || '',
          isPrimary: true
        }] : [],
        
        // Données médicales complètes - structure compatible avec ViewClientModal
        healthSummary: {
          generalHealth: assessment.health?.generalHealth || '',
          mainPathologies: assessment.health?.mainPathologies || '',
          medicationCount: assessment.health?.medications?.count || null,
          fallRisk: assessment.health?.mobility?.fallRisk || '',
          otherHealthIssues: assessment.health?.conditions?.join(', ') || ''
        },
        
        // Données d'autonomie
        autonomyAssessment: {
          autonomyScore: calculateAutonomyScore(assessment.autonomy),
          needsHelp: identifyNeedsHelp(assessment.autonomy),
          lastAssessmentDate: new Date()
        },
        
        // Données de logement
        housingSituation: {
          type: assessment.housing?.type || '',
          status: assessment.housing?.status || '',
          floor: assessment.housing?.floor || null,
          hasElevator: assessment.housing?.hasElevator || false,
          numberOfRooms: assessment.housing?.numberOfRooms || null,
          adaptations: assessment.housing?.adaptations || [],
          hasRisks: assessment.housing?.safety?.hasRisks || false,
          risksDescription: assessment.housing?.safety?.risksDescription || ''
        },
        
        // Données psychologiques
        psychologicalStatus: {
          mood: assessment.psychological?.mood || '',
          feelings: assessment.psychological?.feelings || [],
          activities: assessment.psychological?.activities || [],
          hasFollowUp: assessment.psychological?.hasFollowUp || false,
          followUpProvider: assessment.psychological?.followUpProvider || ''
        },
        
        // Besoins en services
        serviceNeeds: {
          estimatedWeeklyHours: assessment.needs?.schedule?.weeklyHours || null,
          frequency: assessment.needs?.schedule?.frequency || '',
          requiredServices: assessment.needs?.services?.personalCare?.join(', ') || ''
        },
        
        // Métadonnées
        lastQuestionnaireUpdate: new Date(),
        questionnaireCompletionDate: new Date(),
        selfAssessed: true
      };

      await applyProfileEnrichment(db, client.id, enrichmentData);
      await loadAssessment();
      alert('Votre auto-évaluation a été enregistrée et synchronisée avec votre profil !');
    } catch (error) {
      console.error('Erreur lors de la finalisation:', error);
      alert('Erreur lors de l\'enregistrement de votre évaluation');
    } finally {
      setSaving(false);
    }
  };

  // Fonction pour calculer le score d'autonomie
  const calculateAutonomyScore = (autonomyData) => {
    if (!autonomyData) return null;

    const categories = ['hygiene', 'nutrition', 'mobility', 'housekeeping'];
    let totalActivities = 0;
    let autonomousActivities = 0;

    categories.forEach(category => {
      if (autonomyData[category]) {
        Object.values(autonomyData[category]).forEach(value => {
          if (typeof value === 'string' && ['A', 'D', 'AP', 'AT', 'N'].includes(value)) {
            totalActivities++;
            if (value === 'A') autonomousActivities += 1;
            else if (value === 'D') autonomousActivities += 0.7;
            else if (value === 'AP') autonomousActivities += 0.4;
            else if (value === 'AT') autonomousActivities += 0.1;
          }
        });
      }
    });

    return totalActivities > 0 ? Math.round((autonomousActivities / totalActivities) * 100) : null;
  };

  // Fonction pour identifier les domaines nécessitant de l'aide
  const identifyNeedsHelp = (autonomyData) => {
    if (!autonomyData) return {};

    const needsHelp = { 
      hygiene: false, 
      nutrition: false, 
      mobility: false, 
      housekeeping: false
    };

    Object.keys(needsHelp).forEach(domain => {
      if (autonomyData[domain]) {
        const values = Object.values(autonomyData[domain]);
        needsHelp[domain] = values.some(v => ['D', 'AP', 'AT', 'N'].includes(v));
      }
    });

    return needsHelp;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement de votre évaluation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="client-self-assessment">
      {/* En-tête avec progression */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mon auto-évaluation</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Complétez votre évaluation trimestrielle pour mettre à jour vos informations
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {completionPercentage}%
              </span>
            </div>
          </div>
        </div>

        {/* Navigation des sections */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {sections.map(section => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            const hasData = assessment?.[section.id] && Object.values(assessment[section.id]).some(v => 
              v !== '' && v !== null && v !== false && (Array.isArray(v) ? v.length > 0 : true)
            );

            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex flex-col items-center p-3 rounded-lg border transition-colors ${
                  isActive 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <Icon size={20} className="mb-2" />
                <span className="text-xs font-medium text-center">{section.label}</span>
                {hasData && <CheckCircle size={12} className="text-green-500 mt-1" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu de la section active */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {sections.find(s => s.id === activeSection)?.label}
            </h2>
          </div>
          <button
            onClick={() => saveSection(activeSection)}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>

        {/* Formulaire simplifié */}
        <div className="space-y-4">
          {activeSection === 'prealables' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date de l'évaluation
                </label>
                <input
                  type="date"
                  value={assessment?.prealables?.evaluationDate || ''}
                  onChange={(e) => handleFieldChange('prealables', 'evaluationDate', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Durée estimée
                </label>
                <input
                  type="text"
                  value={assessment?.prealables?.evaluationDuration || ''}
                  onChange={(e) => handleFieldChange('prealables', 'evaluationDuration', e.target.value)}
                  placeholder="Ex: 30 minutes"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {activeSection === 'health' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  État de santé général
                </label>
                <select
                  value={assessment?.health?.generalHealth || ''}
                  onChange={(e) => handleFieldChange('health', 'generalHealth', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner</option>
                  <option value="Très bon">Très bon</option>
                  <option value="Bon">Bon</option>
                  <option value="Moyen">Moyen</option>
                  <option value="Fragile">Fragile</option>
                  <option value="Précaire">Précaire</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pathologies principales
                </label>
                <input
                  type="text"
                  value={assessment?.health?.mainPathologies || ''}
                  onChange={(e) => handleFieldChange('health', 'mainPathologies', e.target.value)}
                  placeholder="Ex: Hypertension, diabète..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {activeSection === 'contacts' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact d'urgence - Nom
                </label>
                <input
                  type="text"
                  value={assessment?.contacts?.emergencyContact?.name || ''}
                  onChange={(e) => handleFieldChange('contacts', 'emergencyContact', {
                    ...assessment.contacts.emergencyContact,
                    name: e.target.value
                  })}
                  placeholder="Nom complet"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Lien familial
                </label>
                <input
                  type="text"
                  value={assessment?.contacts?.emergencyContact?.relationship || ''}
                  onChange={(e) => handleFieldChange('contacts', 'emergencyContact', {
                    ...assessment.contacts.emergencyContact,
                    relationship: e.target.value
                  })}
                  placeholder="Ex: Fils, fille, conjoint..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={assessment?.contacts?.emergencyContact?.phone1 || ''}
                  onChange={(e) => handleFieldChange('contacts', 'emergencyContact', {
                    ...assessment.contacts.emergencyContact,
                    phone1: e.target.value
                  })}
                  placeholder="+33 1 23 45 67 89"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {activeSection === 'familialStatus' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Situation familiale
                </label>
                <select
                  value={assessment?.familialStatus?.maritalStatus || ''}
                  onChange={(e) => handleFieldChange('familialStatus', 'maritalStatus', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner</option>
                  <option value="Célibataire">Célibataire</option>
                  <option value="Marié(e)">Marié(e)</option>
                  <option value="Pacsé(e)">Pacsé(e)</option>
                  <option value="Divorcé(e)">Divorcé(e)</option>
                  <option value="Veuf/Veuve">Veuf/Veuve</option>
                  <option value="Concubinage">Concubinage</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Avec qui vivez-vous ?
                </label>
                <div className="space-y-2">
                  {['Seul(e)', 'Conjoint', 'Enfants', 'Famille', 'Colocataire'].map(option => (
                    <label key={option} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={assessment?.familialStatus?.livingWith?.includes(option) || false}
                        onChange={(e) => {
                          const current = assessment?.familialStatus?.livingWith || [];
                          const updated = e.target.checked 
                            ? [...current, option]
                            : current.filter(item => item !== option);
                          handleFieldChange('familialStatus', 'livingWith', updated);
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre d'enfants
                </label>
                <input
                  type="number"
                  min="0"
                  value={assessment?.familialStatus?.numberOfChildren || ''}
                  onChange={(e) => handleFieldChange('familialStatus', 'numberOfChildren', parseInt(e.target.value) || null)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {activeSection === 'legalProtection' && (
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={assessment?.legalProtection?.hasProtection || false}
                    onChange={(e) => handleFieldChange('legalProtection', 'hasProtection', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Avez-vous une protection juridique ?
                  </span>
                </label>
              </div>
              
              {assessment?.legalProtection?.hasProtection && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type de protection
                    </label>
                    <select
                      value={assessment?.legalProtection?.protectionType || ''}
                      onChange={(e) => handleFieldChange('legalProtection', 'protectionType', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Curatelle">Curatelle</option>
                      <option value="Tutelle">Tutelle</option>
                      <option value="Sauvegarde de justice">Sauvegarde de justice</option>
                      <option value="Mandat de protection future">Mandat de protection future</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Représentant légal</h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={assessment?.legalProtection?.representative?.name || ''}
                        onChange={(e) => handleFieldChange('legalProtection', 'representative', {
                          ...assessment.legalProtection.representative,
                          name: e.target.value
                        })}
                        placeholder="Nom du représentant"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <input
                        type="text"
                        value={assessment?.legalProtection?.representative?.organization || ''}
                        onChange={(e) => handleFieldChange('legalProtection', 'representative', {
                          ...assessment.legalProtection.representative,
                          organization: e.target.value
                        })}
                        placeholder="Organisme"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <input
                        type="tel"
                        value={assessment?.legalProtection?.representative?.phone || ''}
                        onChange={(e) => handleFieldChange('legalProtection', 'representative', {
                          ...assessment.legalProtection.representative,
                          phone: e.target.value
                        })}
                        placeholder="Téléphone"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === 'housing' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type de logement
                </label>
                <select
                  value={assessment?.housing?.type || ''}
                  onChange={(e) => handleFieldChange('housing', 'type', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner</option>
                  <option value="Maison individuelle">Maison individuelle</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Résidence service">Résidence service</option>
                  <option value="EHPAD">EHPAD</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Étage
                </label>
                <input
                  type="number"
                  min="0"
                  value={assessment?.housing?.floor || ''}
                  onChange={(e) => handleFieldChange('housing', 'floor', parseInt(e.target.value) || null)}
                  placeholder="0 pour rez-de-chaussée"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={assessment?.housing?.hasElevator || false}
                    onChange={(e) => handleFieldChange('housing', 'hasElevator', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ascenseur disponible
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Adaptations du logement
                </label>
                <div className="space-y-2">
                  {['Barres d\'appui', 'Rampes d\'accès', 'Douche à l\'italienne', 'Siège de douche', 'Éclairage adapté', 'Sol antidérapant'].map(adaptation => (
                    <label key={adaptation} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={assessment?.housing?.adaptations?.includes(adaptation) || false}
                        onChange={(e) => {
                          const current = assessment?.housing?.adaptations || [];
                          const updated = e.target.checked 
                            ? [...current, adaptation]
                            : current.filter(item => item !== adaptation);
                          handleFieldChange('housing', 'adaptations', updated);
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{adaptation}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'autonomy' && (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Cette section vous aide à évaluer votre autonomie dans les activités quotidiennes.
                  Pour chaque activité, indiquez votre niveau d'autonomie.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Hygiène personnelle */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300">Hygiène personnelle</h4>
                  {[
                    { id: 'completeWash', label: 'Toilette complète' },
                    { id: 'faceHands', label: 'Visage et mains' },
                    { id: 'showerBath', label: 'Douche/bain' },
                    { id: 'dressingUpper', label: 'Habillage haut' },
                    { id: 'dressingLower', label: 'Habillage bas' }
                  ].map(activity => (
                    <div key={activity.id}>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {activity.label}
                      </label>
                      <select
                        value={assessment?.autonomy?.hygiene?.[activity.id] || ''}
                        onChange={(e) => handleFieldChange('autonomy', 'hygiene', {
                          ...assessment.autonomy?.hygiene,
                          [activity.id]: e.target.value
                        })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Sélectionner</option>
                        <option value="A">Autonome</option>
                        <option value="D">Difficultés</option>
                        <option value="AP">Aide partielle</option>
                        <option value="AT">Aide totale</option>
                        <option value="N">Non applicable</option>
                      </select>
                    </div>
                  ))}
                </div>

                {/* Alimentation */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300">Alimentation</h4>
                  {[
                    { id: 'prepareMeal', label: 'Préparer un repas' },
                    { id: 'reheatMeal', label: 'Réchauffer un repas' },
                    { id: 'eatAlone', label: 'Manger seul(e)' },
                    { id: 'cutFood', label: 'Couper les aliments' },
                    { id: 'drinkAlone', label: 'Boire seul(e)' }
                  ].map(activity => (
                    <div key={activity.id}>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {activity.label}
                      </label>
                      <select
                        value={assessment?.autonomy?.nutrition?.[activity.id] || ''}
                        onChange={(e) => handleFieldChange('autonomy', 'nutrition', {
                          ...assessment.autonomy?.nutrition,
                          [activity.id]: e.target.value
                        })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Sélectionner</option>
                        <option value="A">Autonome</option>
                        <option value="D">Difficultés</option>
                        <option value="AP">Aide partielle</option>
                        <option value="AT">Aide totale</option>
                        <option value="N">Non applicable</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Mobilité */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300">Mobilité</h4>
                  {[
                    { id: 'getUpFromBed', label: 'Se lever du lit' },
                    { id: 'lieDown', label: 'Se coucher' },
                    { id: 'getUpFromChair', label: 'Se lever d\'une chaise' },
                    { id: 'stairs', label: 'Monter/descendre les escaliers' },
                    { id: 'moveIndoor', label: 'Se déplacer à l\'intérieur' }
                  ].map(activity => (
                    <div key={activity.id}>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {activity.label}
                      </label>
                      <select
                        value={assessment?.autonomy?.mobility?.[activity.id] || ''}
                        onChange={(e) => handleFieldChange('autonomy', 'mobility', {
                          ...assessment.autonomy?.mobility,
                          [activity.id]: e.target.value
                        })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Sélectionner</option>
                        <option value="A">Autonome</option>
                        <option value="D">Difficultés</option>
                        <option value="AP">Aide partielle</option>
                        <option value="AT">Aide totale</option>
                        <option value="N">Non applicable</option>
                      </select>
                    </div>
                  ))}
                </div>

                {/* Activités domestiques */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300">Activités domestiques</h4>
                  {[
                    { id: 'dailyTidying', label: 'Rangement quotidien' },
                    { id: 'dusting', label: 'Dépoussiérage' },
                    { id: 'vacuuming', label: 'Aspirateur' },
                    { id: 'dishes', label: 'Vaisselle' },
                    { id: 'laundry', label: 'Linge' }
                  ].map(activity => (
                    <div key={activity.id}>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {activity.label}
                      </label>
                      <select
                        value={assessment?.autonomy?.housekeeping?.[activity.id] || ''}
                        onChange={(e) => handleFieldChange('autonomy', 'housekeeping', {
                          ...assessment.autonomy?.housekeeping,
                          [activity.id]: e.target.value
                        })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Sélectionner</option>
                        <option value="A">Autonome</option>
                        <option value="D">Difficultés</option>
                        <option value="AP">Aide partielle</option>
                        <option value="AT">Aide totale</option>
                        <option value="N">Non applicable</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'psychological' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Humeur générale
                </label>
                <select
                  value={assessment?.psychological?.mood || ''}
                  onChange={(e) => handleFieldChange('psychological', 'mood', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner</option>
                  <option value="Très bonne">Très bonne</option>
                  <option value="Bonne">Bonne</option>
                  <option value="Moyenne">Moyenne</option>
                  <option value="Morose">Morose</option>
                  <option value="Dépressive">Dépressive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Activités pratiquées
                </label>
                <div className="space-y-2">
                  {['Lecture', 'Télévision', 'Jardinage', 'Bricolage', 'Sorties', 'Visites', 'Sport', 'Autre'].map(activity => (
                    <label key={activity} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={assessment?.psychological?.activities?.includes(activity) || false}
                        onChange={(e) => {
                          const current = assessment?.psychological?.activities || [];
                          const updated = e.target.checked 
                            ? [...current, activity]
                            : current.filter(item => item !== activity);
                          handleFieldChange('psychological', 'activities', updated);
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{activity}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={assessment?.psychological?.hasFollowUp || false}
                    onChange={(e) => handleFieldChange('psychological', 'hasFollowUp', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Suivi psychologique ou psychiatrique
                  </span>
                </label>
              </div>
            </div>
          )}

          {activeSection === 'currentAids' && (
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={assessment?.currentAids?.financialAids?.apa?.has || false}
                    onChange={(e) => handleFieldChange('currentAids', 'financialAids', {
                      ...assessment.currentAids?.financialAids,
                      apa: {
                        ...assessment.currentAids?.financialAids?.apa,
                        has: e.target.checked
                      }
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bénéficiez-vous de l'APA (Allocation Personnalisée d'Autonomie) ?
                  </span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={assessment?.currentAids?.financialAids?.pch?.has || false}
                    onChange={(e) => handleFieldChange('currentAids', 'financialAids', {
                      ...assessment.currentAids?.financialAids,
                      pch: {
                        ...assessment.currentAids?.financialAids?.pch,
                        has: e.target.checked
                      }
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bénéficiez-vous de la PCH (Prestation de Compensation du Handicap) ?
                  </span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={assessment?.currentAids?.homeHelp?.has || false}
                    onChange={(e) => handleFieldChange('currentAids', 'homeHelp', {
                      ...assessment.currentAids?.homeHelp,
                      has: e.target.checked
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Avez-vous actuellement une aide à domicile ?
                  </span>
                </label>
              </div>
            </div>
          )}

          {activeSection === 'needs' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quels services souhaiteriez-vous ?
                </label>
                <div className="space-y-2">
                  {['Aide à la toilette', 'Aide à l\'habillage', 'Préparation des repas', 'Ménage léger', 'Ménage lourd', 'Courses', 'Accompagnement', 'Stimulation cognitive'].map(service => (
                    <label key={service} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={assessment?.needs?.services?.personalCare?.includes(service) || false}
                        onChange={(e) => {
                          const current = assessment?.needs?.services?.personalCare || [];
                          const updated = e.target.checked 
                            ? [...current, service]
                            : current.filter(item => item !== service);
                          handleFieldChange('needs', 'services', {
                            ...assessment.needs?.services,
                            personalCare: updated
                          });
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{service}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fréquence souhaitée
                </label>
                <select
                  value={assessment?.needs?.schedule?.frequency || ''}
                  onChange={(e) => handleFieldChange('needs', 'schedule', {
                    ...assessment.needs?.schedule,
                    frequency: e.target.value
                  })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner</option>
                  <option value="Quotidien">Quotidien</option>
                  <option value="Plusieurs fois par semaine">Plusieurs fois par semaine</option>
                  <option value="Hebdomadaire">Hebdomadaire</option>
                  <option value="Bi-hebdomadaire">Bi-hebdomadaire</option>
                  <option value="Mensuel">Mensuel</option>
                </select>
              </div>
            </div>
          )}

          {activeSection === 'project' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quels sont vos objectifs principaux ?
                </label>
                <div className="space-y-2">
                  {['Maintenir mon autonomie', 'Améliorer ma santé', 'Retrouver une vie sociale', 'Sécuriser mon logement', 'Organiser mon quotidien', 'Préparer l\'avenir'].map(objective => (
                    <label key={objective} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={assessment?.project?.objectives?.includes(objective) || false}
                        onChange={(e) => {
                          const current = assessment?.project?.objectives || [];
                          const updated = e.target.checked 
                            ? [...current, objective]
                            : current.filter(item => item !== objective);
                          handleFieldChange('project', 'objectives', updated);
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{objective}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Avez-vous un aidant familial ?
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={assessment?.project?.caregiver?.hasCaregiver || false}
                    onChange={(e) => handleFieldChange('project', 'caregiver', {
                      ...assessment.project?.caregiver,
                      hasCaregiver: e.target.checked
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Oui, j'ai un aidant familial
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Bouton de finalisation */}
        {completionPercentage >= 80 && (
          <div className="mt-8 text-center">
            <button
              onClick={completeSelfAssessment}
              disabled={saving}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Finalisation...' : 'Finaliser mon auto-évaluation'}
            </button>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Votre évaluation sera automatiquement synchronisée avec votre profil
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientSelfAssessment;
